import { useCallback, useEffect, useState } from 'react';
import { Loader2, Search, Trash2 } from 'lucide-react';
import client, { apiError } from './adminApi';
import { Pagination, StatusBadge, ConfirmDialog } from './components/ui';
import { CrmBadge, CrmSyncPanel } from './components/CrmSync';
import { cn } from '@/lib/utils';

const BOOKING_STATUSES = ['confirmed', 'completed', 'no_show', 'cancelled', 'rescheduled'];

const dublin = (iso) =>
  new Date(iso).toLocaleString('en-IE', {
    timeZone: 'Europe/Dublin', weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });

export default function BookingsPage() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const deleteBooking = async () => {
    if (!selected) return;
    try {
      await client.delete(`/admin/booking/bookings/${selected.bookingId}`, { params: { confirm: true } });
      setConfirmDelete(false);
      setSelected(null);
      load();
    } catch (err) {
      setConfirmDelete(false);
      setError(apiError(err, 'Could not delete the booking.'));
    }
  };
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await client.get('/admin/booking/bookings', {
        params: { q: q || undefined, status: status || undefined, page, per_page: 20 },
      });
      setData(res.data);
    } catch (err) {
      setError(apiError(err, 'Bookings could not be loaded.'));
    }
  }, [q, status, page]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (bookingId, newStatus) => {
    setError('');
    try {
      await client.patch(`/admin/booking/bookings/${bookingId}`, { status: newStatus });
      setMsg(`Booking marked ${newStatus.replace('_', ' ')}.`);
      setConfirmCancel(false);
      await load();
      setSelected(null);
    } catch (err) {
      setError(apiError(err));
      setConfirmCancel(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await client.patch(`/admin/booking/bookings/${selected.bookingId}`, { internalNote: note.trim() });
      setNote('');
      setMsg('Note added.');
      await load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <div data-testid="admin-bookings">
      <h1 className="text-2xl font-extrabold tracking-tight text-navy-900">Bookings</h1>
      <p className="mt-1 text-sm text-slate-500">Website appointment bookings. Private notes are never public.</p>

      <div className="mt-5 flex flex-wrap gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input type="search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name, business, reference…" aria-label="Search bookings" data-testid="bookings-search" className="h-10 w-72 rounded-md border border-slate-300 bg-white pl-9 pr-3 text-sm" />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} aria-label="Filter by status" data-testid="bookings-status-filter" className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm font-semibold">
          <option value="">All statuses</option>
          {BOOKING_STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
      </div>

      {msg && <p role="status" className="mt-3 rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-semibold text-teal-800">{msg}</p>}
      {error && <p role="alert" className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">{error}</p>}

      {!data ? (
        <div className="flex justify-center py-16" role="status"><Loader2 className="h-6 w-6 animate-spin text-navy-900" /></div>
      ) : data.items.length === 0 ? (
        <p className="mt-6 rounded-xl border-2 border-dashed border-slate-300 bg-white p-10 text-center text-sm font-semibold text-slate-500" data-testid="bookings-empty">No bookings found.</p>
      ) : (
        <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                <th scope="col" className="px-4 py-3">Reference</th>
                <th scope="col" className="px-4 py-3">Customer</th>
                <th scope="col" className="px-4 py-3">When (Irish time)</th>
                <th scope="col" className="px-4 py-3">Status</th>
                <th scope="col" className="px-4 py-3">Email</th>
                <th scope="col" className="px-4 py-3"><span className="sr-only">Open</span></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((b) => (
                <tr key={b.bookingId} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-xs font-bold text-navy-900">{b.reference}</td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-navy-900">{b.customer.name}</p>
                    <p className="text-xs text-slate-500">{b.customer.businessName}</p>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{dublin(b.startUtc)}</td>
                  <td className="px-4 py-3">
                    <span className={cn('rounded-full px-2.5 py-1 text-[11px] font-bold uppercase', b.status === 'confirmed' ? 'bg-teal-100 text-teal-800' : b.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600')}>
                      {b.status.replace('_', ' ')}
                    </span>
                    <CrmBadge record={b} />
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{b.emailStatus === 'not_configured' ? 'Not configured' : b.emailStatus}</td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => { setSelected(b); setMsg(''); }} data-testid={`booking-open-${b.reference}`} className="text-sm font-bold text-navy-900 underline-offset-2 hover:underline">
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && <Pagination page={page} perPage={20} total={data.total} onPage={setPage} />}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-950/60 p-4" role="dialog" aria-modal="true" aria-labelledby="booking-detail-title">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6" data-testid="booking-detail">
            <h2 id="booking-detail-title" className="text-lg font-extrabold text-navy-900">
              {selected.reference} — {selected.meetingTypeSnapshot?.name}
            </h2>
            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Customer</dt><dd className="font-semibold text-navy-900">{selected.customer.name}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Business</dt><dd className="font-semibold text-navy-900">{selected.customer.businessName}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Email</dt><dd className="font-semibold text-navy-900">{selected.customer.email}</dd></div>
              {selected.customer.phone && <div className="flex justify-between"><dt className="text-slate-500">Phone</dt><dd className="font-semibold text-navy-900">{selected.customer.phone}</dd></div>}
              <div className="flex justify-between"><dt className="text-slate-500">Area</dt><dd className="font-semibold capitalize text-navy-900">{selected.customer.areaOfInterest}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">When</dt><dd className="font-semibold text-navy-900">{dublin(selected.startUtc)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Source</dt><dd className="font-semibold text-navy-900">{selected.source}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">CRM sync</dt><dd className="font-semibold text-navy-900">{selected.crmSyncStatus}</dd></div>
            </dl>
            {selected.customer.context && (
              <p className="mt-3 rounded-md bg-slate-50 p-3 text-xs leading-relaxed text-slate-600">{selected.customer.context}</p>
            )}
            {(selected.internalNotes || []).map((n, i) => (
              <p key={i} className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
                <strong>{n.by}:</strong> {n.text}
              </p>
            ))}

            <CrmSyncPanel kind="booking" record={selected} onUpdate={(item) => { setSelected(item); load(); }} />

            <div className="mt-4">
              <label htmlFor="booking-note" className="text-xs font-bold text-navy-900">Private internal note</label>
              <div className="mt-1 flex gap-2">
                <input id="booking-note" value={note} onChange={(e) => setNote(e.target.value)} className="w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm" placeholder="Only visible to the Amden team" />
                <button type="button" onClick={addNote} data-testid="booking-add-note" className="shrink-0 rounded-md bg-navy-900 px-3 py-2 text-xs font-bold text-white">Add</button>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {selected.status === 'confirmed' && (
                <>
                  <button type="button" onClick={() => updateStatus(selected.bookingId, 'completed')} data-testid="booking-mark-completed" className="rounded-md bg-teal-700 px-3 py-2 text-xs font-bold text-white hover:bg-teal-600">Mark Completed</button>
                  <button type="button" onClick={() => updateStatus(selected.bookingId, 'no_show')} data-testid="booking-mark-noshow" className="rounded-md border border-slate-300 px-3 py-2 text-xs font-bold text-navy-900 hover:bg-navy-50">Mark No-Show</button>
                  <button type="button" onClick={() => setConfirmCancel(true)} data-testid="booking-cancel" className="rounded-md border border-red-300 px-3 py-2 text-xs font-bold text-red-700 hover:bg-red-50">Cancel Booking</button>
                </>
              )}
              <button type="button" onClick={() => setConfirmDelete(true)} data-testid="booking-delete" className="inline-flex items-center gap-1.5 rounded-md border border-red-300 px-3 py-2 text-xs font-bold text-red-700 hover:bg-red-50">
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Delete
              </button>
              <button type="button" onClick={() => setSelected(null)} className="ml-auto rounded-md border border-slate-300 px-3 py-2 text-xs font-bold text-navy-900 hover:bg-navy-50">Close</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmCancel}
        title="Cancel this booking?"
        message="The customer will no longer have this appointment and the time slot is released back to public availability."
        destructive
        confirmLabel="Cancel Booking"
        onCancel={() => setConfirmCancel(false)}
        onConfirm={() => updateStatus(selected?.bookingId, 'cancelled')}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this booking?"
        message={selected?.crmSyncStatus === 'synced'
          ? 'This booking was synced to Amden Reach. Deleting removes only the website copy — the Reach record stays and must be removed in Reach separately. This cannot be undone.'
          : 'This permanently deletes the booking record. This cannot be undone.'}
        destructive
        confirmLabel="Delete Permanently"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={deleteBooking}
      />
    </div>
  );
}
